import { useCallback } from 'react'
import { InterviewStatus } from '@bosmart/types'
import { clearPromptCache } from '@/services/prompts'
import { logger } from '@/lib/logging'
import { finalizeRecording } from './finalizeRecording'
import type { InterviewSessionOptions, SessionRefs, SessionSetters, SessionUiState } from './types'

interface UseEndInterviewArgs
  extends Pick<InterviewSessionOptions, 'interviewId' | 'token' | 'onEnd'> {
  refs: SessionRefs
  state: SessionUiState
  setters: SessionSetters
  stopAllStreams: (cleanupRecording?: boolean) => Promise<void>
  showNotification: (message: string) => void
}

export function useEndInterview({
  refs,
  state,
  setters,
  interviewId,
  token,
  onEnd,
  stopAllStreams,
  showNotification,
}: UseEndInterviewArgs) {
  return useCallback(async () => {
    setters.setShowConfirmation(false)
    setters.setStatus(InterviewStatus.ENDED)
    refs.shouldReconnectRef.current = false
    refs.isIntentionallyDisconnectingRef.current = true

    if (refs.reconnectTimeoutRef.current) clearTimeout(refs.reconnectTimeoutRef.current)
    refs.reconnectTimeoutRef.current = null

    await disconnectService(refs)
    if (refs.frameIntervalRef.current) clearInterval(refs.frameIntervalRef.current)
    refs.frameIntervalRef.current = null

    setters.setIsTimerRunning(false)
    setters.setShowThankYou(true)

    await finalizeRecording({
      refs,
      history: state.history,
      duration: state.duration,
      interviewId,
      token,
      showNotification,
    })

    await stopAllStreams(false)
    clearPromptCache(interviewId)
    void updateInterviewDuration(interviewId, state.duration, token)

    setTimeout(() => {
      onEnd(state.history, undefined)
    }, 3000)
  }, [
    interviewId,
    onEnd,
    refs,
    setters,
    showNotification,
    state.duration,
    state.history,
    stopAllStreams,
    token,
  ])
}

async function disconnectService(refs: SessionRefs) {
  if (!refs.serviceRef.current) return

  try {
    logger.info('Disconnecting Gemini Live service to stop AI')
    await refs.serviceRef.current.disconnect()
    refs.serviceRef.current = null
  } catch (err) {
    logger.error('Error disconnecting service', { error: String(err) })
  } finally {
    refs.isIntentionallyDisconnectingRef.current = false
  }
}

async function updateInterviewDuration(interviewId: string, duration: number, token?: string) {
  try {
    const { updateInterviewStatus } = await import('@/lib/api-client')
    updateInterviewStatus(interviewId, duration, token).catch((err) => {
      logger.error('Failed to update interview status', { error: String(err) })
    })
  } catch (err) {
    logger.error('Failed to load api-client module', { error: String(err) })
  }
}
